/**
 * App shell: session bootstrap + routing.
 *
 * Session state lives here rather than in a data library because it is the single piece of global
 * state. Everything else uses the small `useAsync` hook (see lib/useAsync.ts).
 */

import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { fetchMe, type SessionUser } from './lib/api';
import { ImportPage } from './pages/ImportPage';
import { LibraryPage } from './pages/LibraryPage';
import { ListDetailPage } from './pages/ListDetailPage';
import { ListsPage } from './pages/ListsPage';
import { LoginPage } from './pages/LoginPage';
import { NewListPage } from './pages/NewListPage';
import { RecipeDetailPage } from './pages/RecipeDetailPage';

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let active = true;
    fetchMe()
      .then((resolved) => {
        if (active) setUser(resolved);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setBooting(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (booting) {
    return (
      <Center h="100vh">
        <Loader color="white" />
      </Center>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onSignedIn={setUser} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const signOut = () => setUser(null);

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/" element={<LibraryPage user={user} onSignOut={signOut} />} />
      <Route path="/import" element={<ImportPage user={user} onSignOut={signOut} />} />
      <Route path="/recipes/:id" element={<RecipeDetailPage user={user} onSignOut={signOut} />} />
      <Route path="/lists" element={<ListsPage user={user} onSignOut={signOut} />} />
      <Route path="/lists/new" element={<NewListPage user={user} onSignOut={signOut} />} />
      <Route path="/lists/:id" element={<ListDetailPage user={user} onSignOut={signOut} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
